/**
 * API 接口统一导出
 * 在这里定义所有的 API 接口
 */
import request from './request'

/**
 * 验证token是否有效
 */
export const validateToken = () => {
  return request({
    url: '/api/login/validate',
    method: 'post'
  })
}

/**
 * 注销登录
 */
export const logout = () => {
  return request({
    url: '/api/login/logout',
    method: 'post'
  })
}

/**
 * 刷新token
 */
export const refreshToken = () => {
  return request({
    url: '/api/login/refresh',
    method: 'post'
  })
}

/**
 * 获取当前登录用户信息（含邮箱，用于待办邮箱通知展示）
 */
export const getUserCurrent = () => {
  return request({
    url: '/api/userInfo/current',
    method: 'get'
  })
}

// 支付记录相关接口
/**
 * 查询支付记录列表
 * @param {String} leftTime - 开始时间
 * @param {String} rightTime - 结束时间
 * @param {Number} typeId - 类型ID，默认为0
 */
export const queryPayRecordList = (params) => {
  return request({
    url: '/api/payRecord/queryPayRecordList',
    method: 'get',
    params: {
      leftTime: params.leftTime,
      rightTime: params.rightTime,
      typeId: params.typeId || 0
    }
  })
}

/**
 * 查询支付记录类型列表
 */
export const queryPayRecordTypeList = () => {
  return request({
    url: '/api/payRecordType/queryPayRecordTypeList',
    method: 'get'
  })
}

/**
 * 新增支付记录
 * @param {Object} data - 支付记录数据
 * @param {String} data.payTime - 支付时间
 * @param {Number} data.typeId - 类型ID
 * @param {Number} data.amount - 金额
 * @param {String} data.description - 描述
 */
export const addPayRecord = (data) => {
  return request({
    url: '/api/payRecord/addPayRecord',
    method: 'post',
    data
  })
}

/**
 * 上传支付记录 Excel（导入）
 * @param {File} file - Excel 文件
 */
export const uploadPayRecord = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/api/payRecord/uploadPayRecord',
    method: 'post',
    data: formData
  })
}

/**
 * 更新支付记录类型
 * @param {Object} data - 类型数据
 * @param {Number} data.id - 类型ID
 * @param {String} data.name - 类型名称
 */
export const updatePayRecordType = (data) => {
  return request({
    url: `/api/payRecordType/updatePayRecordType`,
    method: 'post',
    data: data
  })
}

/**
 * 添加支付记录类型
 * @param {Object} data - 类型数据
 * @param {String} data.name - 类型名称
 */
export const addPayRecordType = (data) => {
  return request({
    url: `/api/payRecordType/addPayRecordType`,
    method: 'post',
    data: data
  })
}

/**
 * 删除支付记录类型
 * @param {Object} data - 类型数据
 * @param {Number} data.id - 类型ID
 * @param {Number} data.newTypeId - 新类型ID（用于换绑已有记录）
 */
export const deletePayRecordType = (data) => {
  return request({
    url: `/api/payRecordType/deletePayRecordType`,
    method: 'post',
    data: data
  })
}

/**
 * 更新支付记录
 * @param {Object} data - 支付记录数据
 * @param {Number} data.id - 记录ID
 * @param {Number} data.amount - 金额（分）
 * @param {Number} data.typeId - 类型ID
 * @param {String} data.payTime - 支付时间
 * @param {String} data.description - 描述
 */
export const updatePayRecord = (data) => {
  return request({
    url: '/api/payRecord/updatePayRecord',
    method: 'post',
    data
  })
}

/**
 * 删除支付记录
 * @param {Number} id - 记录ID
 */
export const deletePayRecord = (id) => {
  return request({
    url: '/api/payRecord/deletePayRecord',
    method: 'post',
    params: { id }
  })
}

/**
 * 导出支出记录为 Excel（上传 OSS，返回下载链接）
 * @param {String} leftTime - 开始日期 yyyy-MM-dd
 * @param {String} rightTime - 结束日期 yyyy-MM-dd
 * @param {Number} typeId - 类型 ID，0 为全部
 */
export const exportPayRecord = (params) => {
  return request({
    url: '/api/payRecord/exportPayRecord',
    method: 'post',
    params: { leftTime: params.leftTime, rightTime: params.rightTime, typeId: params.typeId || 0, format: params.format || 'xlsx' }
  })
}

/**
 * 获取统计数据（按年份）
 * @param {Number} year - 年份（可选）
 */
export const getStatisticsData = (year) => {
  return request({
    url: '/api/payRecord/getStatisticsData',
    method: 'get',
    params: year ? { year } : {}
  })
}

/**
 * 刷新统计数据缓存
 * @param {Number} year - 年份（可选）
 */
export const refreshStatisticsCache = (year) => {
  return request({
    url: '/api/payRecord/refreshStatisticsCache',
    method: 'post',
    params: year ? { year } : {}
  })
}

/**
 * 刷新所有统计数据缓存
 */
export const refreshAllStatisticsCache = () => {
  return request({
    url: '/api/payRecord/refreshAllStatisticsCache',
    method: 'post'
  })
}

/**
 * 获取日期选择器数据
 * @param {String} type - 类型（year/month）
 */
export const getDateSelectorData = (type) => {
  return request({
    url: '/api/payRecord/getDateSelectorData',
    method: 'get',
    params: { type }
  })
}

/**
 * 刷新日期选择器缓存
 */
export const refreshDateSelectorCache = () => {
  return request({
    url: '/api/payRecord/refreshDateSelectorCache',
    method: 'post'
  })
}

/**
 * 获取指定年份的每日支出汇总（当年消费热力图）
 * @param {Number} year - 年份，不传则当前年
 */
export const getYearExpenditureHeatmap = (year) => {
  return request({
    url: '/api/payRecord/getYearExpenditureHeatmap',
    method: 'get',
    params: year != null ? { year } : {}
  })
}

// 待办记录相关接口
/**
 * 新增待办记录
 * @param {Object} data - 待办记录数据
 */
export const addTodoRecord = (data) => {
  return request({
    url: '/api/todoRecord/addTodoRecord',
    method: 'post',
    data
  })
}

/**
 * 更新待办记录
 * @param {Object} data - 待办记录数据
 */
export const updateTodoRecord = (data) => {
  return request({
    url: '/api/todoRecord/updateTodoRecord',
    method: 'post',
    data
  })
}

/**
 * 删除待办记录
 * @param {Number} id - 记录ID
 */
export const deleteTodoRecord = (id) => {
  return request({
    url: '/api/todoRecord/deleteTodoRecord',
    method: 'post',
    params: { id }
  })
}

/**
 * 查询待办记录列表（不分页，返回日期范围内全部记录）
 * @param {Object} params - 查询参数 { startDate, endDate, calendarType?, status?, priority? }
 */
export const queryTodoRecordList = (params) => {
  return request({
    url: '/api/todoRecord/queryTodoRecordList',
    method: 'get',
    params
  })
}

/**
 * 查询某一天的待办记录
 * @param {String} date - 日期
 * @param {String} calendarType - 日历类型
 */
export const queryTodoRecordsByDate = (date, calendarType) => {
  return request({
    url: '/api/todoRecord/queryTodoRecordsByDate',
    method: 'get',
    params: { date, calendarType }
  })
}

/**
 * 完成待办记录
 * @param {Number} id - 记录ID
 */
export const completeTodoRecord = (id) => {
  return request({
    url: '/api/todoRecord/completeTodoRecord',
    method: 'post',
    params: { id }
  })
}

// 考勤打卡相关接口
/**
 * 打卡（上班/下班）
 * @param {Object} data - { punchType: 'CHECK_IN'|'CHECK_OUT', location?, remark? }
 */
export const attendancePunch = (data) => {
  return request({
    url: '/api/attendance/punch',
    method: 'post',
    data
  })
}

/**
 * 获取今日打卡情况
 */
export const getAttendanceToday = () => {
  return request({
    url: '/api/attendance/today',
    method: 'get'
  })
}

/**
 * 按日期范围查询打卡记录
 * @param {String} startDate - yyyy-MM-dd
 * @param {String} endDate - yyyy-MM-dd
 */
export const getAttendanceList = (params) => {
  return request({
    url: '/api/attendance/list',
    method: 'get',
    params
  })
}

/**
 * 补卡（指定日期和时间）
 * @param {Object} data - { punchDate: 'yyyy-MM-dd', punchType: 'CHECK_IN'|'CHECK_OUT', punchTime: 'HH:mm', remark? }
 */
export const attendancePatch = (data) => {
  return request({
    url: '/api/attendance/patch',
    method: 'post',
    data
  })
}

/**
 * 修改打卡记录（某天的上班/下班时间、备注）
 * @param {{ punchDate, checkInTime, checkOutTime, remark }} data
 */
export const updateAttendance = (data) => {
  return request({
    url: '/api/attendance/update',
    method: 'put',
    data
  })
}

/**
 * 工作时长统计 - 日视图（某月每天）
 * @param {String} month - yyyy-MM
 */
export const getWorkTimeStatsByDay = (month) => {
  return request({
    url: '/api/attendance/worktime/day',
    method: 'get',
    params: { month }
  })
}

/**
 * 删除某天的全部打卡记录
 * @param {{ punchDate: string }} data
 */
export const deleteAttendanceByDate = (data) => {
  return request({
    url: '/api/attendance/delete',
    method: 'post',
    data
  })
}

// 工资记录相关接口
/**
 * 获取工资记录列表
 */
export const getSalaryList = () => {
  return request({
    url: '/api/salary/list',
    method: 'get'
  })
}

/**
 * 获取工资记录详情
 * @param {Number} id - 记录ID
 */
export const getSalaryDetail = (id) => {
  return request({
    url: '/api/salary/detail',
    method: 'get',
    params: { id }
  })
}

/**
 * 添加工资记录
 * @param {Object} data - 工资记录数据
 */
export const addSalaryRecord = (data) => {
  return request({
    url: '/api/salary/add',
    method: 'post',
    data
  })
}

/**
 * 更新工资记录
 * @param {Object} data - 工资记录数据
 */
export const updateSalaryRecord = (data) => {
  return request({
    url: '/api/salary/update',
    method: 'post',
    data
  })
}

/**
 * 删除工资记录
 * @param {Number} id - 记录ID
 */
export const deleteSalaryRecord = (id) => {
  return request({
    url: '/api/salary/delete',
    method: 'post',
    params: { id }
  })
}

/**
 * 获取工资类型列表
 */
export const getSalaryTypeList = () => {
  return request({
    url: '/api/salary/types',
    method: 'get'
  })
}

/**
 * 获取公司信息列表
 */
export const getCompanyList = () => {
  return request({
    url: '/api/salary/companies',
    method: 'get'
  })
}

/**
 * 根据条件获取工资记录列表
 * @param {Object} params - 查询参数
 * @param {String} params.companyName - 公司名称
 * @param {String} params.year - 年份
 * @param {String} params.typeName - 类型名称
 */
export const getSalaryListByConditions = (params) => {
  return request({
    url: '/api/salary/listByConditions',
    method: 'get',
    params
  })
}

/**
 * 导出工资记录为 Excel
 */
export const exportSalaryRecord = (params) => {
  return request({
    url: '/api/salary/export',
    method: 'post',
    params: params || {}
  })
}

/**
 * 导入工资记录 Excel
 */
export const importSalaryRecord = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/api/salary/import',
    method: 'post',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

/**
 * 获取工资记录可用年份列表
 */
export const getSalaryAvailableYears = () => {
  return request({
    url: '/api/salary/availableYears',
    method: 'get'
  })
}

// 图片上传相关接口

/**
 * 上传单个图片到OSS并获取图片信息
 * @param {FormData} data - 包含图片文件的FormData对象
 */
export const uploadSingleImage = (data) => {
  return request({
    url: '/api/image/upload',
    method: 'post',
    data,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 批量上传图片到OSS
 * @param {FormData} data - 包含图片文件列表的FormData对象
 */
export const uploadImages = (data) => {
  return request({
    url: '/api/image/uploadForSalaryRecord',
    method: 'post',
    data,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// 用户管理相关接口
/**
 * 获取用户列表（仅ROOT用户）
 */
export const getUserList = () => {
  return request({
    url: '/api/user/list',
    method: 'get'
  })
}

/**
 * 获取用户详情（仅ROOT用户）
 * @param {Number} id - 用户ID
 */
export const getUserDetail = (id) => {
  return request({
    url: '/api/user/detail',
    method: 'get',
    params: { id }
  })
}

/**
 * 添加用户（仅ROOT用户）
 * @param {Object} data - 用户数据
 */
export const addUser = (data) => {
  return request({
    url: '/api/user/add',
    method: 'post',
    data
  })
}

/**
 * 更新用户（仅ROOT用户）
 * @param {Object} data - 用户数据
 */
export const updateUser = (data) => {
  return request({
    url: '/api/user/update',
    method: 'post',
    data
  })
}

/**
 * 删除用户（仅ROOT用户）
 * @param {Number} id - 用户ID
 */
export const deleteUser = (id) => {
  return request({
    url: '/api/user/delete',
    method: 'post',
    params: { id }
  })
}

// 登录管理相关接口
/**
 * 获取所有未过期的SESSION列表（仅ROOT用户）
 */
export const getSessionList = () => {
  return request({
    url: '/api/session/list',
    method: 'get'
  })
}

/**
 * 强制一个用户的SESSION超时（仅ROOT用户）
 * @param {Number} sessionId - SESSION ID
 */
export const forceLogout = (sessionId) => {
  return request({
    url: '/api/session/forceLogout',
    method: 'post',
    params: { sessionId }
  })
}

// 登录日志相关接口
/**
 * 获取登录日志列表（仅ROOT用户）
 */
export const getLoginLogList = () => {
  return request({
    url: '/api/loginLog/list',
    method: 'get'
  })
}

// 借贷记录相关接口
/**
 * 查询借贷记录列表（分页）
 * @param {String} borrowerName - 借款人姓名（可选）
 * @param {String} startDate - 开始日期（可选）
 * @param {String} endDate - 结束日期（可选）
 * @param {Number} pageNum - 页码
 * @param {Number} pageSize - 每页数量
 */
export const queryLoanRecordList = (params) => {
  return request({
    url: '/api/loanRecord/queryLoanRecordList',
    method: 'get',
    params
  })
}

/**
 * 获取所有借款人名称列表
 */
export const getAllBorrowerNames = () => {
  return request({
    url: '/api/loanRecord/getAllBorrowerNames',
    method: 'get'
  })
}

/**
 * 新增借贷记录
 * @param {Object} data - 借贷记录数据
 */
export const addLoanRecord = (data) => {
  return request({
    url: '/api/loanRecord/addLoanRecord',
    method: 'post',
    data
  })
}

/**
 * 更新借贷记录
 * @param {Object} data - 借贷记录数据
 */
export const updateLoanRecord = (data) => {
  return request({
    url: '/api/loanRecord/updateLoanRecord',
    method: 'post',
    data
  })
}

/**
 * 删除借贷记录
 * @param {Number} id - 记录ID
 */
export const deleteLoanRecord = (id) => {
  return request({
    url: '/api/loanRecord/deleteLoanRecord',
    method: 'post',
    params: { id }
  })
}

/**
 * 获取借贷统计数据
 */
export const getLoanStatistics = () => {
  return request({
    url: '/api/loanRecord/getStatistics',
    method: 'get'
  })
}

/**
 * 导出借贷记录为 Excel
 */
export const exportLoanRecord = (params) => {
  return request({
    url: '/api/loanRecord/export',
    method: 'post',
    params: params || {}
  })
}

/**
 * 导入借贷记录 Excel
 */
export const importLoanRecord = (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/api/loanRecord/import',
    method: 'post',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

// 账号密码管理相关接口
/**
 * 查询账号密码列表（分页）
 */
export const queryAccountPasswordList = (params) => {
  return request({
    url: '/api/accountPassword/queryList',
    method: 'get',
    params
  })
}

/**
 * 获取所有应用名称列表
 */
export const getAllAccountPasswordAppNames = () => {
  return request({
    url: '/api/accountPassword/getAllAppNames',
    method: 'get'
  })
}

/**
 * 新增账号密码
 */
export const addAccountPassword = (data) => {
  return request({
    url: '/api/accountPassword/add',
    method: 'post',
    data
  })
}

/**
 * 更新账号密码
 */
export const updateAccountPassword = (data) => {
  return request({
    url: '/api/accountPassword/update',
    method: 'post',
    data
  })
}

/**
 * 删除账号密码
 */
export const deleteAccountPassword = (id) => {
  return request({
    url: '/api/accountPassword/delete',
    method: 'post',
    params: { id }
  })
}

/**
 * 获取单条详情（含账号、密码），仅点击「显示」时调用
 */
export const getAccountPasswordDetail = (id) => {
  return request({
    url: '/api/accountPassword/getDetail',
    method: 'get',
    params: { id }
  })
}

/**
 * 导出账号密码为 Excel（异步，请到文件管理中下载）
 */
export const exportAccountPassword = () => {
  return request({
    url: '/api/accountPassword/export',
    method: 'post'
  })
}

// 文件与图片管理（仅 ROOT）
export const getFileManagementList = (params) => {
  return request({
    url: '/api/fileManagement/list',
    method: 'get',
    params: params || {}
  })
}

export const getExportDownloadUrl = (id) => {
  return request({
    url: '/api/fileManagement/downloadUrl/export',
    method: 'get',
    params: { id }
  })
}

/**
 * 通过后端代理下载文件（推荐：线上与本地均可用，不依赖预签名链接）
 * @param {number} id 导出文件 id
 * @param {string} fileName 保存时的文件名（来自列表 record.fileName）
 */
export const downloadExportFile = (id, fileName) => {
  return request({
    url: '/api/fileManagement/download/export',
    method: 'get',
    params: { id },
    responseType: 'blob'
  }).then(blob => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName || 'download.xlsx'
    a.click()
    URL.revokeObjectURL(url)
  })
}

export const getImageDownloadUrl = (id) => {
  return request({
    url: '/api/fileManagement/downloadUrl/image',
    method: 'get',
    params: { id }
  })
}

// 文档相关接口
/**
 * 获取当前用户的文档树
 */
export const getDocumentTree = () => {
  return request({
    url: '/api/document/tree',
    method: 'get'
  })
}

/**
 * 获取节点元数据（不含正文）
 */
export const getDocumentNode = (id) => {
  return request({
    url: '/api/document/node',
    method: 'get',
    params: { id }
  })
}

/**
 * 获取文档正文
 */
export const getDocumentContent = (id) => {
  return request({
    url: '/api/document/content',
    method: 'get',
    params: { id }
  })
}

/**
 * 创建节点（文件夹或文档）
 * @param {Object} data - { parentId?, nodeType: 'FOLDER'|'DOC', docType?: 'TEXT'|'SHEET', name?, icon?, sortOrder? }
 */
export const createDocumentNode = (data) => {
  return request({
    url: '/api/document/create',
    method: 'post',
    data
  })
}

/**
 * 重命名节点
 * @param {Object} data - { id, name, icon? }
 */
export const renameDocumentNode = (data) => {
  return request({
    url: '/api/document/rename',
    method: 'post',
    data
  })
}

/**
 * 移动节点
 * @param {Object} data - { id, parentId?, sortOrder? }
 */
export const moveDocumentNode = (data) => {
  return request({
    url: '/api/document/move',
    method: 'post',
    data
  })
}

/**
 * 收藏/取消收藏
 * @param {Object} data - { id, isStarred: 0|1 }
 */
export const starDocumentNode = (data) => {
  return request({
    url: '/api/document/star',
    method: 'post',
    data
  })
}

/**
 * 保存文档正文
 * @param {Object} data - { id, content }
 */
export const saveDocumentContent = (data) => {
  return request({
    url: '/api/document/save',
    method: 'post',
    data
  })
}

/**
 * 删除节点（文件夹将级联删除子节点）
 * @param {Object} data - { id }
 */
export const deleteDocumentNode = (data) => {
  return request({
    url: '/api/document/delete',
    method: 'post',
    data
  })
}


